<script setup lang="ts">
    import KForm from "@/components/forms/Form/KForm.vue";
    import { flashWarning } from "@/utils";
    import { AxiosError } from "axios";
    import { computed, onBeforeMount, onBeforeUnmount, ref, toRefs, watch } from "vue";
    import { useStore } from "@/store";
    import emitter from "tiny-emitter/instance";
    import { useKForm } from "@/composables/useKForm";

    // TODO Ändra till användning av type i materialModule i Vue3
    interface Material {
        id?: string;
        artikelnummer: string;
        namn: string;
        leverantor?: string;
        streckkod1?: string;
        streckkod2?: string;
    }

    const store = useStore();

    const props = defineProps<{
        tabId: number; // TODO Ändra i Vue3
        parameters?: {
            materialId: string;
        };
    }>();

    const { parameters, tabId } = toRefs(props);
    const formRef = ref();
    const { inputErrors, formErrors, handleErrorResponse } = useKForm();

    const isEditing = computed(() => !!material.value);
    const material = computed(() =>
        store.getters["material/materialById"]({
            id: parameters?.value?.materialId,
        })
    );

    const hasUnsavedData = computed(() => formRef.value?.hasUnsavedData());
    const heading = computed(() => (isEditing.value ? "Redigera material" : "Skapa material"));
    const submitLabel = computed(() => (isEditing.value ? "Redigera material" : "Lägg till material"));

    defineExpose({
        hasUnsavedData,
        tabId,
    });

    // TODO convert to useTabs in v3
    const closeTab = ({
        override,
        hasUnsavedData,
    }: {
        override?: boolean;
        hasUnsavedData?: boolean;
    } = {}) => {
        if (!override && hasUnsavedData) {
            emitter.emit("open-dialog", {
                type: "warning",
                description: "Du har osparad data. Vill du stänga utan att spara?",
                buttons: [
                    {
                        title: "Fortsätt redigera",
                        type: "secondary",
                    },
                    {
                        title: "Ja, stäng utan att spara",
                        type: "primary",
                        action: () => {
                            store.dispatch("tabs/closeTab", tabId.value);
                        },
                    },
                ],
            });

            return;
        }

        store.dispatch("tabs/closeTab", tabId.value);
    };

    const onSubmit = async (newMaterial: unknown) => {
        try {
            if (isEditing.value) {
                await store.dispatch("material/updateMaterial", { material: newMaterial as Material });
            } else {
                await store.dispatch("material/createMaterial", { material: newMaterial as Material });
            }

            closeTab({ override: true });
        } catch (thrownError) {
            const error = thrownError as AxiosError;

            if (error?.response?.data) {
                handleErrorResponse(error);
                return;
            }

            flashWarning(isEditing.value ? "Kunde inte redigera materialet" : "Kunde inte lägga till nya materialet");
        }
    };

    const onCancel = async ({ hasUnsavedData }: { hasUnsavedData: boolean }) => {
        closeTab({ hasUnsavedData });
    };

    const initForm = async () => {
        if (!parameters?.value?.materialId) {
            return null;
        }

        const retrievedMaterial = await store.dispatch("material/getMaterialById", {
            id: parameters.value.materialId,
        });

        return retrievedMaterial;
    };

    const fetchLeverantorer = async ({ query }: { query?: string } = {}) => {
        try {
            const filterParams: { q?: string } = {};

            if (query) {
                filterParams.q = query;
            }

            const { subsetName } = await store.dispatch("leverantor/fetchLeverantorer", {
                page: 1,
                limit: 10,
                filterParams,
            });

            const leverantorer = store.getters["leverantor/leverantorerSubset"]({
                subsetName,
                offset: 0,
                limit: 10,
            });

            return leverantorer.map((lev: { id: number; namn: string }) => ({
                value: lev.id,
                label: lev.namn,
            }));
        } catch (error) {
            console.error("Kunde inte hämta leverantörer:", error);
            flashWarning("Kunde inte hämta leverantörer");
            return [];
        }
    };

    const setTabName = () => {
        store.commit("tabs/setTabName", {
            id: tabId.value,
            name: isEditing.value ? "Redigera material" : "Skapa material",
        });
    };

    onBeforeMount(async () => {
        await store.dispatch("leverantor/addSubscriber");
        await store.dispatch("material/addSubscriber");
        setTabName();
    });

    onBeforeUnmount(async () => {
        await store.dispatch("leverantor/removeSubscriber");
        await store.dispatch("material/removeSubscriber");
    });

    watch(material, (newMaterial) => {
        setTabName();
    });
</script>

<template>
    <KForm
        ref="formRef"
        id="material-form"
        class="material-form"
        :initial-values-getter="initForm"
        :submit-content="submitLabel"
        cancel-content="Avbryt"
        :disableSubmit="!hasUnsavedData"
        :form-errors="formErrors"
        :errors="inputErrors"
        columns="3"
        @submit="onSubmit"
        @cancel="onCancel"
    >
        <template #header>
            <h2>{{ heading }}</h2>
        </template>
        <template #default>
            <div class="form-content">
                <div class="form-section">
                    <KText type="text" name="namn" label="Namn" class="span-one" validation="required" />
                    <KText
                        type="text"
                        label="Artikelnummer"
                        name="artikelnummer"
                        validation="required"
                        class="span-one"
                    />
                </div>
                <KText type="text" label="Streckkod 1" name="streckkod1" />
                <KText type="text" label="Streckkod 2" name="streckkod2" />
                <KText type="text" label="Lokalt artikelnummer" name="lokalt_artikelnummer" />
                <KAutocomplete
                    name="leverantor"
                    label="Leverantör"
                    :options="fetchLeverantorer"
                    :on-search="fetchLeverantorer"
                    class="span-one"
                />
            </div>
        </template>
    </KForm>
</template>

<style lang="scss" scoped></style>
